/**
 * React Query hooks for the B1.0 surfaces.
 *
 * Every hook is gated on `VITE_USE_LIVE_API === "true"` via the `enabled`
 * flag — so with the flag unset, no fetch ever fires and the providers
 * stay on fixture/localStorage data (identical to today's demo). Staleness
 * per `docs/backlog/b1.0-react-query-and-typed-resources.md`.
 *
 * Consumed *inside* the existing Context providers (gateways / dashboards /
 * alarms), not by route components directly — the providers keep their
 * public API so consumers stay oblivious.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";
import { useToast } from "@/lib/ui/toast";
import { errorCodeOf, statusOf } from "./error";
import type { TwinBatch, TwinEntity } from "./types";
import { fetchAllPages } from "./paginate";
import { THINGS_PAGE_MAX, things } from "./things";
import { packsApi } from "./packs";
import { dashboards } from "./dashboards";
import { alarms } from "./alarms";
import { computedTags } from "./computed-tags";
import { listOrgTags, twinBindings } from "./twin-bindings";
import { twinEntities } from "./twin-entities";
import { twinConnections } from "./twin-connections";
import { twinBatches } from "./twin-batches";
import { twinCorrelations } from "./twin-correlations";
import {
  twinCorrelationGroups,
  type CorrelationGroupInput,
} from "./twin-correlation-groups";
import {
  VOCABULARY_BINDINGS_KEY,
  VOCABULARY_TARGETS_KEY,
  twinDefectTypes,
  twinVocabulary,
  withOrgDefects,
  type OrgDefectType,
  type OrgDefectTypeInput,
  type VocabBindingInput,
  type VocabTargets,
} from "./twin-vocabulary";
import { ANALYSIS_JOBS_KEY } from "./analysis-jobs";
import {
  DEFECT_WORDS_KEY,
  twinDefectWords,
  type DefectDetailsInput,
  type DefectPhotoInput,
  type DefectSubcategoryInput,
} from "./twin-defect-words";
import { twinCapability, type CapabilityRequest } from "./twin-capability";
import { twinGenealogy } from "./twin-genealogy";
import { twinLots } from "./twin-lots";
import { twinHeatEvents } from "./twin-events";
import { twinStages } from "./twin-stages";
import { plantTemplateInstallError, twinPlantTemplates } from "./twin-plant-templates";
import { installedMessage, provisionedMessage } from "@/lib/plant/provision-words";
import {
  twinPlants,
  type PlantInfo,
  type ProvisionPlantRequest,
} from "./twin-plants";
import { twinRuns } from "./twin-runs";
import { twinCommonality, type CommonalityRequest } from "./twin-commonality";
import { twinMaterials } from "./twin-materials";
import {
  twinImport,
  type DataSourceCreate,
  type ImportProfileCreate,
  type ImportProfileUpdate,
  type ImportPreviewRequest,
  type ImportApplyRequest,
} from "./twin-import";

/** Build-time flag: only when explicitly "true" do live fetches fire. */
export const isLiveApi = import.meta.env.VITE_USE_LIVE_API === "true";

/**
 * Live fetches require BOTH the build flag AND an authenticated session.
 * Without the auth gate the providers (mounted above the router) fire on
 * the public /login + /signup pages, 401, and spam "backend unreachable"
 * toasts before the user has even signed in. In stub mode isAuthenticated
 * is always true, so local dev behaviour is unchanged.
 */
function useLiveEnabled(): boolean {
  const { isAuthenticated } = useAuth();
  const { activeOrgId } = useOrg();
  // Every /v1/iot/* + /v1/twin/* endpoint is org-scoped and 400s with
  // `org_required` when X-Org-Id is absent. Don't enable a fetch until an
  // org is resolved — belt-and-braces alongside the synchronous header
  // wiring in auth.tsx + org-context.tsx, so a future regression there
  // can't resurrect the fire-before-headers race.
  return isLiveApi && isAuthenticated && activeOrgId !== null;
}

/** How often the Gateways page reads the Things again: every 30 s, every 2 min when they take more than one page. */
export const THINGS_POLL_MS = 30_000;
export const THINGS_POLL_LARGE_MS = 120_000;

/**
 * `poll` reads the list again while the caller is mounted (the Gateways page); the other callers keep the one read.
 * Every caller shares the ["things"] cache entry, and the shortest interval of the mounted callers wins.
 */
export function useThingsQuery(opts?: { poll?: boolean }) {
  return useQuery({
    queryKey: ["things"],
    // List EVERY thing (gateways + sensor children + standalone) — the
    // unified P2.2 surface — every page of 200 (the server's max page size).
    queryFn: () => things.listAll(),
    enabled: useLiveEnabled(),
    staleTime: 30_000,
    refetchInterval: opts?.poll
      ? (query) => ((query.state.data?.items.length ?? 0) > THINGS_PAGE_MAX ? THINGS_POLL_LARGE_MS : THINGS_POLL_MS)
      : undefined,
  });
}

/** How often the Gateways page and the gateway sheet read the rates (the server keeps an org's answer 10 s). */
export const THING_RATES_POLL_MS = 15_000;

/**
 * Every gateway's reading rate and its last 20 minutes (GET /v1/things/rates), polled every 15 s while a caller is
 * mounted and the tab is visible. A failed read says nothing to the person: while the last read failed, the callers
 * show the rate as unknown (React Query keeps the last good answer, so they check isError). An older API sends the
 * path to GET /v1/things/{thing_id}, which answers 400 invalid_thing_id: on a 400 or a 404 the page stops asking;
 * any other failure asks again after a minute, with no quick retries (the server answers a slow read with 503, and
 * retrying at once would only load it more).
 */
export function useThingRatesQuery() {
  return useQuery({
    queryKey: ["things", "rates"],
    queryFn: () => things.rates(),
    enabled: useLiveEnabled(),
    staleTime: 10_000,
    retry: false,
    refetchInterval: (query) => {
      const status = statusOf(query.state.error);
      if (status === 400 || status === 404) return false;
      return query.state.status === "error" ? 60_000 : THING_RATES_POLL_MS;
    },
  });
}

/**
 * The active org's enabled packs (server truth, per-org). Drives the Tier-2
 * (Twin) nav visibility so it reflects THIS org — not the global localStorage
 * tenant-theme that leaked the Twin nav into every org.
 */
export function useEnabledPacksQuery() {
  return useQuery({
    queryKey: ["enabled-packs"],
    queryFn: () => packsApi.listEnabled(),
    enabled: useLiveEnabled(),
    staleTime: 60_000,
  });
}

export function useDashboardsQuery() {
  return useQuery({
    queryKey: ["dashboards"],
    queryFn: () => dashboards.list(),
    enabled: useLiveEnabled(),
    staleTime: 60_000,
  });
}

export function useDashboardQuery(id: string | undefined) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: ["dashboard", id],
    queryFn: () => dashboards.get(id as string),
    enabled: live && !!id,
    staleTime: 0, // editor expects a fresh copy on open
  });
}

export function useAlarmEventsQuery() {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: ["alarm-events"],
    queryFn: () => alarms.events.list({ status: "firing" }),
    enabled: live,
    staleTime: 5_000,
    refetchInterval: live ? 10_000 : false,
    // Alarms must surface even when the ops/alarms screen isn't the focused
    // tab — React Query pauses interval refetch on blur by default, which made
    // a freshly-fired alarm only appear on a manual reload. Poll regardless.
    refetchIntervalInBackground: true,
  });
}

export function useAlarmRulesQuery() {
  return useQuery({
    queryKey: ["alarm-rules"],
    queryFn: () => alarms.rules.list(),
    enabled: useLiveEnabled(),
    staleTime: 30_000,
  });
}

/** Computed-tag defs for the active org — exported so writes can invalidate. */
export const COMPUTED_TAGS_KEY = ["computed-tags"] as const;

/**
 * The org's computed-tag defs (#165). Feeds `ComputedTagsProvider`, which is what
 * the /app/computed-tags page and the dashboard alias picker read.
 */
export function useComputedTagsQuery() {
  return useQuery({
    queryKey: COMPUTED_TAGS_KEY,
    queryFn: () => computedTags.list(),
    enabled: useLiveEnabled(),
    staleTime: 30_000,
  });
}

/**
 * The org's full tag catalogue — the input-tag picker on the computed-tag editor.
 *
 * listOrgTags walks every page (the server clamps a page at 200), so an org past 200
 * tags gets its whole catalogue. A server-side search parameter is still the better
 * fix for very large orgs; this keeps the list complete until then.
 */
export const ORG_TAG_CATALOG_KEY = ["org-tag-catalog"] as const;

export function useOrgTagCatalogQuery() {
  return useQuery({
    queryKey: ORG_TAG_CATALOG_KEY,
    queryFn: () => listOrgTags(),
    enabled: useLiveEnabled(),
    staleTime: 60_000,
  });
}

/** Query keys for the Tier-2 plant model — exported so mutations can
 *  invalidate them after a write-through. */
export const TWIN_ENTITIES_KEY = ["twin-entities"] as const;
export const TWIN_CONNECTIONS_KEY = ["twin-connections"] as const;

export function useTwinEntitiesQuery() {
  return useQuery({
    queryKey: TWIN_ENTITIES_KEY,
    // Pull the whole plant in one page — the demo graph is ~15 nodes;
    // 200 is the server's max page size.
    queryFn: () => twinEntities.list({ limit: 200 }),
    enabled: useLiveEnabled(),
    staleTime: 30_000,
  });
}

/**
 * Every item of the plant, page by page (fetchAllPages), for a page that judges a binding against the plant: one page
 * stops at 200 and its total says nothing of the rest, so a reader bound past it would read as gone (#1406 review).
 * Keyed under TWIN_ENTITIES_KEY, so whatever refreshes the plant's items refreshes these too.
 */
export function useAllTwinEntitiesQuery() {
  return useQuery({
    queryKey: [...TWIN_ENTITIES_KEY, "all"] as const,
    queryFn: () => fetchAllPages<TwinEntity>("/v1/twin/entities"),
    enabled: useLiveEnabled(),
    staleTime: 30_000,
  });
}

export function useTwinConnectionsQuery() {
  return useQuery({
    queryKey: TWIN_CONNECTIONS_KEY,
    queryFn: () => twinConnections.list({ limit: 200 }),
    enabled: useLiveEnabled(),
    staleTime: 30_000,
  });
}

/** Query key for the org's whole binding list. The alarm "where" resolver (lib/alarms/where.ts) reads under the same
 *  key, so the two share one fetch. */
export const TWIN_BINDINGS_ALL_KEY = ["twin-bindings", "all"] as const;

/**
 * Every equipment binding of the active org, in ONE request: `GET /v1/twin/bindings` with no `entity_id` answers the
 * whole list (the handler does not page it), which is what lets a screen tell "this machine has no tag" from "I only
 * read the first page". Its readers grey out equipment without a tag, so they must treat anything but `isSuccess`
 * as "unknown", never as "none".
 */
export function useTwinBindingsQuery() {
  return useQuery({
    queryKey: TWIN_BINDINGS_ALL_KEY,
    queryFn: () => twinBindings.list(),
    enabled: useLiveEnabled(),
    // Read again on every mount and window focus (#1236 review, MEDIUM): the go-live, add-equipment and sieve flows
    // bind tags without touching this key, and a machine bound a moment ago must not stay greyed as "No tags".
    staleTime: 0,
  });
}

/** Query key for the heat list — exported so mutations (e.g. a heat run)
 *  can invalidate it after a write-through. */
export const TWIN_BATCHES_KEY = ["twin-batches"] as const;

/** Server page size. httpx.LimitOffset caps a request at 200, so this is the ceiling
 *  per round-trip, not a preference. Exported so a caller can tell a full first page (more
 *  heats behind it) from a plant's whole list. */
export const BATCH_PAGE = 200;

/** Stop after this many pages (= 10,000 heats). A backstop against a runaway loop, not
 *  an expected limit; if a real org ever trips it the list is visibly truncated and we
 *  should move this fetch server-side rather than raise the number. */
const BATCH_MAX_PAGES = 50;

/** The recent-heats list. Deliberately still ONE page.
 *
 *  Six surfaces read this, and five of them want a handful of rows: the process graph
 *  reads items[0], the replay player one .find, correlations one heat's shift, parts a
 *  picker, the batches table paginates client-side. Only the statistical report needs
 *  every heat — see useAllTwinBatchesQuery.
 *
 *  Keeping it at one page also keeps the dashboard KPIs defined as they are today: the
 *  aggregate builders (twin-aggregates.ts) call computeReport with NO date window, so
 *  this cap is the de-facto recency bound for yield %, rejection % and the defect
 *  Pareto on the landing page. Widening it here would silently redefine those numbers
 *  for every org — including under the Pareto widget's own "last 30 days" title. */
export function useTwinBatchesQuery() {
  return useQuery({
    queryKey: TWIN_BATCHES_KEY,
    queryFn: () => twinBatches.list({ limit: BATCH_PAGE }),
    enabled: useLiveEnabled(),
    staleTime: 30_000,
  });
}

/** Query key for the FULL heat walk, distinct from TWIN_BATCHES_KEY. The writes that invalidate the heat list
 *  (a Run Heat, an import applied or undone, a plant cleared) invalidate this key too, so the heats list never
 *  shows a replaced heat beside the fresh first page (#956 review L2). An invalidation refetches only an ACTIVE
 *  query: the walk re-runs when the heats list is open over a list longer than one page, and the report (never
 *  enabled) refetches on its next Generate click anyway. */
export const TWIN_BATCHES_ALL_KEY = ["twin-batches-all"] as const;

/** Every heat: the statistical report, and the heats list when its first page is full.
 *
 *  A single limit:200 call was silently wrong for any org with history: the list is
 *  newest-first, so a "16-month report" was computed over roughly the most recent two
 *  months with nothing on screen saying so. The heats list had the same fault: a plant
 *  with more than 200 heats listed 200 (a demo walk, 2026-09-26).
 *
 *  Disabled by default — it does not fire on mount. The report refetch()es it on an
 *  explicit Generate click; the heats list enables it only when its first page came back
 *  full, so the walk happens when a human asks for a report or opens a list longer than
 *  one page, and at no other time: not on login, not on the dashboard, not after every
 *  Run Heat, not on an org switch. That matters because the walk is O(pages²) work server-side (each
 *  page costs limit+offset row visits) and this diff also added trace->>'part' to the
 *  projection, which detoasts each row's trace blob.
 *
 *  Stability depends on ListBatches' `ORDER BY started_at DESC, id DESC` — without the
 *  id tiebreaker, paging a non-unique sort silently duplicates and drops rows. */
export function useAllTwinBatchesQuery(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: TWIN_BATCHES_ALL_KEY,
    queryFn: async () => {
      const items: TwinBatch[] = [];
      let truncated = false;
      for (let page = 0; ; page++) {
        if (page >= BATCH_MAX_PAGES) {
          truncated = true;
          break;
        }
        const res = await twinBatches.list({ limit: BATCH_PAGE, offset: page * BATCH_PAGE });
        const got = res.items ?? [];
        items.push(...got);
        if (got.length < BATCH_PAGE) break;
      }
      // `truncated` is returned, not swallowed. Reporting total = items.length while
      // silently dropping the tail is the exact failure this replaced, one order of
      // magnitude up; the caller surfaces it.
      return { items, total: items.length, truncated };
    },
    enabled: opts.enabled ?? false,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
}

/** Per-heat trace, fetched on drawer-open. Disabled until a heat is picked. */
export function useTwinBatchTraceQuery(heatNumber: string | undefined) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: ["twin-batch-trace", heatNumber],
    queryFn: () => twinBatches.trace(heatNumber as string),
    enabled: live && !!heatNumber,
    staleTime: 60_000,
  });
}

/** Query-key prefix for a heat's versioned source-reading membership (#197,
 *  migration 0104) — exported so a reassignment can invalidate it. */
export const HEAT_READINGS_KEY = ["twin-heat-readings"] as const;

/**
 * A heat's versioned reading↔heat MEMBERSHIP (migration 0104): the source
 * readings assembled into it, resolved as-of now. Live-only, fetched once a
 * heat is selected (drawer open). retry:false — a legacy/greenfield heat may
 * 404 until the endpoint is deployed, and that must not spin.
 */
export function useHeatReadingsQuery(heatId?: string) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: [...HEAT_READINGS_KEY, heatId],
    queryFn: () => twinGenealogy.heatReadings(heatId as string),
    enabled: live && !!heatId,
    staleTime: 30_000,
    retry: false,
  });
}

/** Query-key prefix for the per-heat replay event stream — exported so a heat
 *  run / regenerate can invalidate every heat's events at once. */
export const TWIN_HEAT_EVENTS_KEY = ["twin-heat-events"] as const;

/**
 * The chronological event stream one heat emitted (material-genealogy-real C3)
 * — backs the ReplayPlayer scrubber (FE-B). Self-gated on `useLiveEnabled()`
 * and fetched only once a `heatId` is set (drawer / replay open). New-model
 * only: legacy heats have no events row and the endpoint may 404 until the
 * replay API deploys, so failures are not retried — the caller falls back to
 * the cosmetic walk.
 */
export function useHeatEventsQuery(heatId?: string) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: [...TWIN_HEAT_EVENTS_KEY, heatId],
    queryFn: () => twinHeatEvents.stream(heatId as string),
    enabled: live && !!heatId,
    staleTime: 60_000,
    retry: false,
  });
}

export function useTwinCorrelationsQuery(
  params?: {
    granularity?: "heat" | "lot" | "day" | "week" | "month";
    method?: "pearson" | "spearman";
    from?: string;
    to?: string;
  },
  opts: { enabled?: boolean } = {},
) {
  const live = useLiveEnabled();
  return useQuery({
    // params are part of the key so a granularity/method/window change refetches.
    queryKey: ["twin-correlations", params ?? null],
    queryFn: () => twinCorrelations.get(params),
    enabled: live && (opts.enabled ?? true),
    staleTime: 60_000,
  });
}

// ── Correlation GROUPS (Wave C, Phase 1) ─────────────────────────────
// The 8 standard council groups + the org's custom groups, and one group's
// within-group matrix. A group correlates only its declared inputs × outcomes,
// so cross-group spurious pairs are never computed.

/** The org's vocabulary bindings and its defect words (lib/defect-words reads the words). */
export function useVocabularyBindingsQuery() {
  return useQuery({
    queryKey: VOCABULARY_BINDINGS_KEY,
    queryFn: () => twinVocabulary.list(),
    enabled: useLiveEnabled(),
    staleTime: 5 * 60_000,
  });
}

/** The defects a binding may name: the priors', fixed for a deploy, with the org's own defect types in the same list
 *  (withOrgDefects), so every picker offers them. Adding a type reads it again here; a type another person added
 *  shows within five minutes, as the org's words do. */
export function useVocabularyTargetsQuery() {
  return useQuery({
    queryKey: VOCABULARY_TARGETS_KEY,
    queryFn: () => twinVocabulary.targets(),
    select: withOrgDefects,
    enabled: useLiveEnabled(),
    staleTime: 5 * 60_000,
  });
}

/**
 * Adds one of the org's own defect types (POST, model write). The new type goes into the list a binding may name at
 * once, and the list and the defect words are read again. The caller binds its word to it and says what became of
 * both, so this hook toasts nothing.
 */
export function useCreateDefectType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OrgDefectTypeInput) => twinDefectTypes.create(input),
    onSuccess: ({ item }: { item: OrgDefectType }) => {
      qc.setQueryData<VocabTargets>(VOCABULARY_TARGETS_KEY, (cur) =>
        cur ? { ...cur, org_defects: [...(cur.org_defects ?? []).filter((d) => d.key !== item.key), item] } : cur,
      );
      void qc.invalidateQueries({ queryKey: VOCABULARY_TARGETS_KEY });
      void qc.invalidateQueries({ queryKey: VOCABULARY_BINDINGS_KEY });
    },
  });
}

/**
 * Sets a batch of the org's vocabulary bindings (PUT, all or nothing). The caller says what became of it (the
 * import review keeps an approval standing when its words fail), so this hook toasts nothing. A batch that approves a
 * defect word queues an analysis refresh, so the analysis chip is read again with the words.
 */
export function usePutVocabularyBindings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bindings: VocabBindingInput[]) => twinVocabulary.put(bindings),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: VOCABULARY_BINDINGS_KEY });
      void qc.invalidateQueries({ queryKey: ANALYSIS_JOBS_KEY });
    },
  });
}

// ── A plant's defect words (migration 0271, plan 2026-10-27 F2) ─────────
// Every word the org books, binds or describes, with what the plant says
// about it. Any member reads; the writes are an org admin's. None of them
// moves the analysis, so they invalidate the list alone. The caller says
// what became of a write (the Rejections page, P4), so these toast nothing.

/** The org's defect list: each word's pieces, binding, details, sub categories and reference photos. */
export function useDefectWordsQuery() {
  return useQuery({
    queryKey: DEFECT_WORDS_KEY,
    queryFn: () => twinDefectWords.list(),
    enabled: useLiveEnabled(),
    staleTime: 60_000,
  });
}

/** Sets a word's details as a whole (PUT, org admin). */
export function useSetDefectDetails() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sourceKey, input }: { sourceKey: string; input: DefectDetailsInput }) =>
      twinDefectWords.setDetails(sourceKey, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: DEFECT_WORDS_KEY });
    },
  });
}

/** Adds, edits, retires or unretires a word's sub categories (PUT, org admin; never a delete). */
export function useSetDefectSubcategories() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sourceKey, subcategories }: { sourceKey: string; subcategories: DefectSubcategoryInput[] }) =>
      twinDefectWords.setSubcategories(sourceKey, subcategories),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: DEFECT_WORDS_KEY });
    },
  });
}

/** Adds a reference photo of a word (multipart POST, org admin). */
export function useUploadDefectPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sourceKey, input }: { sourceKey: string; input: DefectPhotoInput }) =>
      twinDefectWords.uploadPhoto(sourceKey, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: DEFECT_WORDS_KEY });
    },
  });
}

/** Removes one of a word's reference photos (DELETE, org admin). */
export function useDeleteDefectPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sourceKey, imageId }: { sourceKey: string; imageId: string }) =>
      twinDefectWords.deletePhoto(sourceKey, imageId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: DEFECT_WORDS_KEY });
    },
  });
}

/** List the standard + custom correlation groups for the active org. */
export function useCorrelationGroupsQuery() {
  return useQuery({
    queryKey: ["twin-correlation-groups"],
    queryFn: () => twinCorrelationGroups.list(),
    enabled: useLiveEnabled(),
    staleTime: 60_000,
  });
}

/** One group's within-group correlation matrix over the org's heats. Disabled
 *  until a group is selected. `method` is part of the key so a toggle refetches. */
export function useCorrelationGroupMatrixQuery(
  id: string | undefined,
  params?: { method?: "pearson" | "spearman"; from?: string; to?: string },
) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: ["twin-correlation-group-matrix", id, params ?? null],
    queryFn: () => twinCorrelationGroups.matrix(id as string, params),
    enabled: live && !!id,
    staleTime: 60_000,
    // A failed read (503) is said with a retry button (CorrelationGroupsPanel): each attempt reloads the plant's
    // heats and lots, so the app-wide retries would run that load three times.
    retry: false,
  });
}

/** Author a custom correlation group. */
export function useCreateCorrelationGroup() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (body: CorrelationGroupInput) => twinCorrelationGroups.create(body),
    onSuccess: (g) => {
      void qc.invalidateQueries({ queryKey: ["twin-correlation-groups"] });
      toast.push("ok", `Created correlation group "${g.name}".`);
    },
    onError: () => {
      toast.push(
        "error",
        "Couldn't create the group — a group with this name may already exist.",
      );
    },
  });
}

/** Edit a custom correlation group (standard groups are read-only). */
export function useUpdateCorrelationGroup() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CorrelationGroupInput }) =>
      twinCorrelationGroups.update(id, body),
    onSuccess: (g) => {
      void qc.invalidateQueries({ queryKey: ["twin-correlation-groups"] });
      void qc.invalidateQueries({ queryKey: ["twin-correlation-group-matrix"] });
      toast.push("ok", `Updated "${g.name}".`);
    },
    onError: () => toast.push("error", "Couldn't update the group."),
  });
}

/** Delete a custom correlation group (standard groups are read-only). */
export function useDeleteCorrelationGroup() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (id: string) => twinCorrelationGroups.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["twin-correlation-groups"] });
      toast.push("ok", "Deleted the correlation group.");
    },
    onError: () => toast.push("error", "Couldn't delete the group."),
  });
}

/** Process-capability + sigma/DPMO for the Six Sigma report (task #368). POST with the
 *  granularity/window + per-CTQ spec map; the body is part of the key so a change refetches. */
export function useTwinCapabilityQuery(body?: CapabilityRequest) {
  return useQuery({
    queryKey: ["twin-capability", body ?? null],
    queryFn: () => twinCapability.compute(body),
    enabled: useLiveEnabled(),
    staleTime: 60_000,
  });
}

/** Per-unit backward genealogy, fetched on Sheet open (M8.1). */
export function usePartTraceQuery(partId: string | undefined) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: ["twin-part-trace", partId],
    queryFn: () => twinGenealogy.partTrace(partId as string),
    enabled: live && !!partId,
    staleTime: 60_000,
  });
}

/**
 * Universal output trace (P1.5) — accepts a unit id (CA-/MO-/TS-…) OR a lot id
 * (L-…). Fetched on demand from the "trace any output" box; disabled until an
 * id is entered.
 */
export function useOutputTraceQuery(outputId: string | undefined) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: ["twin-output-trace", outputId],
    queryFn: () => twinGenealogy.outputTrace(outputId as string),
    enabled: live && !!outputId,
    staleTime: 60_000,
    retry: false, // a 404 (output_not_found) is a user-facing answer, not a flake
  });
}

/**
 * Tracked units across the org for a stage (P1.5). `materialClass` selects the
 * stage; disabled until one is set.
 */
export function useTwinUnitsQuery(materialClass: string | undefined) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: ["twin-units", materialClass],
    queryFn: () =>
      twinGenealogy.units({ material_class: materialClass, limit: 200 }),
    enabled: live && !!materialClass,
    staleTime: 30_000,
  });
}

/**
 * Forward where-used — the units a heat produced (+ aggregate header). Pass a
 * `materialClass` to view the heat's moulds / specimens (defaults to castings
 * server-side).
 */
/** Query-key prefix for the forward where-used (batch parts) reads —
 *  exported so a heat run can invalidate every heat/material variation at
 *  once via this prefix. */
export const TWIN_BATCH_PARTS_KEY = ["twin-batch-parts"] as const;

export function useBatchPartsQuery(
  heatId: string | undefined,
  materialClass?: string,
) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: [...TWIN_BATCH_PARTS_KEY, heatId, materialClass],
    queryFn: () =>
      twinGenealogy.batchParts(heatId as string, {
        material_class: materialClass,
        limit: 200,
      }),
    enabled: live && !!heatId,
    staleTime: 30_000,
  });
}

/** Query key for the lot-first listing (D1) — exported so a heat run /
 *  regenerate can invalidate the lot rows alongside the batch reads. */
export const TWIN_LOT_SUMMARIES_KEY = ["twin-lot-summaries"] as const;

/**
 * The lot-first listing (D1): produced casting lots newest-first, each with
 * its contributing-heat shares + inherited heat/sand/pour attribute ranges +
 * unit pass/fail/defect counts. `materialClass` optionally overrides the
 * produced class (the server defaults to the org's primary part class).
 */
export function useLotSummariesQuery(materialClass?: string) {
  return useQuery({
    queryKey: [...TWIN_LOT_SUMMARIES_KEY, materialClass ?? null],
    queryFn: () =>
      twinLots.summaries({ material_class: materialClass, limit: 200 }),
    enabled: useLiveEnabled(),
    staleTime: 30_000,
  });
}

/** A lot's serialized units — fetched when a lot's detail Sheet opens. */
export function useLotUnitsQuery(lotId: string | undefined) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: ["twin-lot-units", lotId],
    queryFn: () => twinLots.units(lotId as string, { limit: 200 }),
    enabled: live && !!lotId,
    staleTime: 30_000,
  });
}

/**
 * The org's configured output stages (P1.8b) — derived server-side from
 * entities' trackedOutput config. Drives the Parts screen's stage selector.
 * May be EMPTY (or 404 until the endpoint deploys) — the Parts screen falls
 * back to the static OUTPUT_STAGES default, so this is never load-bearing.
 */
/** Query key for the org's configured output stages — exported so the
 *  plant-template install can invalidate it (a fresh plant adds stages). */
export const TWIN_STAGES_KEY = ["twin-stages"] as const;

export function useStagesQuery() {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: TWIN_STAGES_KEY,
    queryFn: () => twinStages.stages({ limit: 200 }),
    enabled: live,
    staleTime: 60_000,
    // The endpoint may not be deployed / backfilled yet — treat any failure as
    // "empty" (the caller falls back) rather than retrying a known-missing route.
    retry: false,
  });
}

/**
 * Installable plant templates (the gallery). Live-gated like the other
 * /v1/twin/* reads — a no-op in stub mode. Foundry-pack + member-gated
 * server-side; an empty list (e.g. pack disabled) renders nothing.
 */
export function usePlantTemplatesQuery() {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: ["twin-plant-templates"],
    queryFn: () => twinPlantTemplates.list(),
    enabled: live,
    staleTime: 5 * 60_000, // catalogue is effectively static
    retry: false,
  });
}

/**
 * One-click plant-template install. On success, invalidates the queries
 * that hydrate the process graph + downstream Twin surfaces so everything
 * repopulates without a manual refresh:
 *   - entities + connections (the plant store re-hydrates → graph populates)
 *   - stages (a fresh plant configures output stages)
 *   - things (the install provisions a gateway + tags, simulated in a demo org;
 *     a real org gets none)
 *
 * Surfaces a success toast with the provisioned counts and an error toast
 * that special-cases the backend guard codes (409 already_installed,
 * 400 cannot_install_onto_reference, 503 plant_template_unavailable); see
 * plantTemplateInstallError.
 */
export function useInstallPlantTemplate() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (id: string) => twinPlantTemplates.install(id),
    onSuccess: (result) => {
      // Repopulate the graph + dependent surfaces. The plant store's own
      // effects re-run off these query results (entities → nodes,
      // connections → edges), so the canvas + live overlay update on settle.
      void qc.invalidateQueries({ queryKey: TWIN_ENTITIES_KEY });
      void qc.invalidateQueries({ queryKey: TWIN_CONNECTIONS_KEY });
      void qc.invalidateQueries({ queryKey: TWIN_STAGES_KEY });
      void qc.invalidateQueries({ queryKey: ["things"] });
      toast.push("ok", installedMessage(result));
    },
    onError: (err) => {
      toast.push("error", plantTemplateInstallError(err));
    },
  });
}

/**
 * Provision a generated plant: the Plant Builder's "Create plant". Writes the
 * client-generated spec (entities + connections, and in a demo or rehearsal org one
 * gateway per equipment + tags + bindings; a real org gets no placeholder gateway or
 * tag) to the active org, then invalidates the graph + Twin surfaces so they
 * repopulate on settle.
 */
export function useProvisionPlant() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (req: ProvisionPlantRequest) => twinPlants.provision(req),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: TWIN_ENTITIES_KEY });
      void qc.invalidateQueries({ queryKey: TWIN_CONNECTIONS_KEY });
      void qc.invalidateQueries({ queryKey: TWIN_STAGES_KEY });
      void qc.invalidateQueries({ queryKey: ["things"] });
      toast.push("ok", provisionedMessage(result));
    },
    onError: () => {
      toast.push("error", "Couldn't create the plant. Please try again.");
    },
  });
}

/**
 * The active org's provisioned plant archetype (#118) — backs the Admin page's
 * per-org archetype display so it shows the org's ACTUAL plant kind (iron
 * green-sand, ductile no-bake, Al HPDC, …) instead of a hardcoded "grey iron"
 * label. Tier-1 + read-only server-side, so available to every role and
 * independent of the foundry pack toggle. Returns `provisioned: false` (not a
 * 404) for orgs with no plants row, so the caller falls back to a default label.
 * Live-gated like the other /v1/twin reads — a no-op in stub mode.
 */
export function useActivePlantQuery() {
  return useQuery<PlantInfo>({
    queryKey: ["twin-plant"],
    queryFn: () => twinPlants.getActive(),
    enabled: useLiveEnabled(),
    staleTime: 5 * 60_000, // an org's plant rarely changes within a session
    retry: false, // the endpoint may predate this deploy — fall back, don't retry
  });
}

/**
 * "Run heat" — generate one complete heat (a batch + its lots / operations /
 * tracked units) for the active org via POST /v1/twin/runs. Synchronous.
 *
 * On success, invalidates the reads that surface the new heat so the stat
 * strip, Batches table and Parts (forward where-used) repopulate without a
 * manual refresh:
 *   - the heat list (TWIN_BATCHES_KEY) — feeds the "Latest heat" stat + Batches
 *   - the batch-parts prefix (TWIN_BATCH_PARTS_KEY) — feeds Parts where-used
 *   - the lot-first listing (TWIN_LOT_SUMMARIES_KEY) — a new heat adds lots
 *   - stages (TWIN_STAGES_KEY) — a fresh heat can surface new output stages
 *
 * Surfaces a success toast with the provisioned counts and an error toast
 * that special-cases the pack-gate (403) and otherwise gives a generic
 * retry message.
 */
export function useRunHeat() {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  return useMutation({
    // The mutate variable is an OPTIONAL recipe id (Phase-2 Wave-0): pass a
    // picked recipe's id to run the heat to that recipe (its pattern's cavity
    // weights drive casting mass), or `undefined` for the no-recipe path. Both
    // the process-graph Run button and the ReplayPlayer call this.
    mutationFn: (recipeId?: string) => twinRuns.run(recipeId),
    onSuccess: (result) => {
      // Refresh the surfaces that show the new heat. React Query re-fetches
      // the affected queries on settle, so the stat strip + Batches/Parts
      // update on their own.
      void qc.invalidateQueries({ queryKey: TWIN_BATCHES_KEY });
      void qc.invalidateQueries({ queryKey: TWIN_BATCHES_ALL_KEY });
      void qc.invalidateQueries({ queryKey: TWIN_BATCH_PARTS_KEY });
      void qc.invalidateQueries({ queryKey: TWIN_LOT_SUMMARIES_KEY });
      void qc.invalidateQueries({ queryKey: TWIN_STAGES_KEY });
      // Offer a one-click jump to the new heat's trace. The Batches screen
      // matches `?heat=` against the human-readable heat NUMBER (that's what
      // its rows key on via toBatchRow), so navigate with heat_number — not
      // heat_id — or the drawer won't auto-open.
      toast.push(
        "ok",
        // Report what the heat ACTUALLY produced. `castings` used to be the total
        // tracked-unit count on any plant-driven heat, so a heat of 122 castings
        // + 122 moulds + 3 specimens announced itself as "247 castings". When a
        // plant's units do not classify as castings/moulds/specimens at all, say
        // "tracked units" rather than calling them castings.
        result.castings || result.moulds || result.specimens
          ? `Generated heat ${result.heat_id} — ${result.castings} castings, ${result.moulds} moulds, ${result.specimens} specimens.`
          : `Generated heat ${result.heat_id} — ${result.units_total ?? 0} tracked units.`,
        {
          action: {
            label: "View heat",
            onClick: () =>
              navigate(`/app/twin/batches?heat=${result.heat_number}`),
          },
        },
      );
    },
    onError: (err) => {
      toast.push("error", runHeatErrorMessage(err));
    },
  });
}

/**
 * "Regenerate heats" — clear the org's synthetic heats and re-emit a fresh batch
 * from the CURRENT plant (plant-driven), so Batches / Parts / Correlations reflect
 * the live process after a model change. Destructive — the caller confirms first.
 * Invalidates all queries on success so every twin surface refetches.
 */
export function useRegenerateHeats() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: () => twinRuns.regenerate(),
    onSuccess: (result) => {
      void qc.invalidateQueries();
      toast.push(
        "ok",
        `Regenerated ${result.heats} heats from the current plant (${result.units} parts). Batches, Parts and Correlations now reflect this process.`,
      );
    },
    onError: (err) => {
      toast.push("error", runHeatErrorMessage(err));
    },
  });
}

/**
 * Map a run failure to a friendly message.
 *
 * This branches on the backend's error CODE, not just the status. A 403 here
 * has two completely different causes and they need opposite actions from the
 * customer:
 *
 *   generation_refused — the org is a REAL plant. Heat generation is disabled
 *     on purpose so a customer's real records are never mixed with fabricated
 *     ones. Nothing is broken and no permission is missing; the answer is to
 *     bring in real production data instead.
 *   anything else (403) — a genuine pack/role gate.
 *
 * Previously EVERY 403 said "foundry pack + member role required", so a real
 * plant refusing to fabricate data told the customer they had a licensing or
 * permissions problem — sending them to hunt for a toggle that was never the
 * cause.
 */
function runHeatErrorMessage(err: unknown): string {
  if (errorCodeOf(err) === "generation_refused") {
    return (
      "This is a real plant, so sample heats are turned off — the twin only " +
      "shows production that actually happened. Import your production data " +
      "or capture it from the floor to populate it."
    );
  }
  if (statusOf(err) === 403) {
    return "You don't have access to run a heat for this org (foundry pack + member role required).";
  }
  return "Couldn't generate a heat. Please try again.";
}

/** Query key for the tenant material classes — exported so the materials
 *  screen's mutations can invalidate after a write. */
export const TWIN_MATERIALS_KEY = ["twin-materials"] as const;

/**
 * Tenant material classes (M8.7) — Tier-1, so available to every org. Empty
 * orgs get the foundry pack's classes as a server-side fallback, so the list
 * is never empty when the pack is loaded.
 */
export function useMaterialsQuery() {
  return useQuery({
    queryKey: TWIN_MATERIALS_KEY,
    queryFn: () => twinMaterials.list(),
    enabled: useLiveEnabled(),
    staleTime: 60_000,
  });
}

/**
 * Defect commonality (M8.4) — the "why did this fail?" group comparison. Fires
 * only when a scope is set (a defect / part / heat is selected); the scope is
 * the cache key so a new selection re-runs the comparison. POST under the hood.
 */
export function useCommonalityQuery(req: CommonalityRequest | undefined) {
  const live = useLiveEnabled();
  const hasScope =
    !!req &&
    (!!req.scope.defect_type || !!req.scope.part_id || !!req.scope.heat_id);
  return useQuery({
    queryKey: ["twin-commonality", req?.scope, req?.dimensions, req?.window_days],
    queryFn: () => twinCommonality.analyze(req as CommonalityRequest),
    enabled: live && hasScope,
    staleTime: 60_000,
  });
}

// ── Customer-template ingestion (Data Sources + import wizard) ───────────────
//
// Query keys exported so the wizard's mutations can invalidate the lists after
// a create/delete/apply write-through.
export const TWIN_DATA_SOURCES_KEY = ["twin-data-sources"] as const;
export const TWIN_IMPORT_PROFILES_KEY = ["twin-import-profiles"] as const;
export const TWIN_IMPORT_RUNS_KEY = ["twin-import-runs"] as const;

/** The org's registered data sources (uploaded files, lab feeds, …). */
export function useDataSourcesQuery() {
  return useQuery({
    queryKey: TWIN_DATA_SOURCES_KEY,
    queryFn: () => twinImport.dataSources.list(),
    enabled: useLiveEnabled(),
    staleTime: 30_000,
  });
}

/** The org's saved, reusable column mappings. */
export function useImportProfilesQuery() {
  return useQuery({
    queryKey: TWIN_IMPORT_PROFILES_KEY,
    queryFn: () => twinImport.profiles.list(),
    enabled: useLiveEnabled(),
    staleTime: 30_000,
  });
}

/** Recent import runs (newest-first server-side) — the ingestion history. */
export function useImportRunsQuery() {
  return useQuery({
    queryKey: TWIN_IMPORT_RUNS_KEY,
    queryFn: () => twinImport.runs.list(),
    enabled: useLiveEnabled(),
    staleTime: 30_000,
  });
}

/**
 * Create a data source. Invalidates the list so the new row appears; callers
 * add their own success/error toast via the mutate options (the wizard creates
 * one silently as part of an import, so a hook-level toast would double up).
 */
export function useCreateDataSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DataSourceCreate) => twinImport.dataSources.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TWIN_DATA_SOURCES_KEY });
    },
  });
}

/** Delete a data source. Invalidates the list on success. */
export function useDeleteDataSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => twinImport.dataSources.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TWIN_DATA_SOURCES_KEY });
    },
  });
}

/** Save a reusable mapping profile. Invalidates the profile list. */
export function useCreateImportProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ImportProfileCreate) => twinImport.profiles.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TWIN_IMPORT_PROFILES_KEY });
    },
  });
}

/** Rename / re-map a saved profile. Invalidates the profile list. */
export function useUpdateImportProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: ImportProfileUpdate }) =>
      twinImport.profiles.update(vars.id, vars.body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TWIN_IMPORT_PROFILES_KEY });
    },
  });
}

/** Delete a saved profile. Invalidates the profile list. */
export function useDeleteImportProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => twinImport.profiles.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TWIN_IMPORT_PROFILES_KEY });
    },
  });
}

/**
 * Dry-run resolution — no persistence, so nothing to invalidate. The caller
 * reads the returned counts / records / warnings straight off the mutation.
 */
export function useImportPreview() {
  return useMutation({
    mutationFn: (body: ImportPreviewRequest) => twinImport.preview(body),
  });
}

/**
 * Persist an import. On success invalidates the run history plus the twin
 * surfaces the write feeds — batches (production rows) and correlations (the
 * measurements they crunch) — so those repopulate without a manual refresh.
 * The caller adds its run-summary toast via the mutate options.
 */
export function useImportApply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ImportApplyRequest) => twinImport.apply(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TWIN_IMPORT_RUNS_KEY });
      void qc.invalidateQueries({ queryKey: TWIN_BATCHES_KEY });
      void qc.invalidateQueries({ queryKey: TWIN_BATCHES_ALL_KEY });
      void qc.invalidateQueries({ queryKey: ["twin-correlations"] });
    },
  });
}

export const TWIN_IMPORT_ORPHANS_KEY = ["twin-import-orphans"] as const;

/**
 * Undo one import run (B2). Everything an import feeds is invalidated, as for an
 * apply, plus the orphan report. The caller toasts: a 409 refusal carries its
 * reason in `detail`, and the result lists what was removed.
 */
export function useUndoImportRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; force?: boolean }) => twinImport.runs.undo(vars.id, vars.force),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TWIN_IMPORT_RUNS_KEY });
      void qc.invalidateQueries({ queryKey: TWIN_BATCHES_KEY });
      void qc.invalidateQueries({ queryKey: TWIN_BATCHES_ALL_KEY });
      void qc.invalidateQueries({ queryKey: ["twin-correlations"] });
      void qc.invalidateQueries({ queryKey: TWIN_IMPORT_ORPHANS_KEY });
    },
  });
}

/**
 * The orphan report: hard classes must read 0 after every import. Fetched on
 * demand (`enabled`) — `deep` scans every imported reading.
 */
export function useImportOrphansQuery(deep: boolean, enabled: boolean) {
  return useQuery({
    queryKey: [...TWIN_IMPORT_ORPHANS_KEY, deep],
    queryFn: () => twinImport.orphans.report(deep),
    enabled: useLiveEnabled() && enabled,
    staleTime: 30_000,
  });
}

/** Reap the provably-safe orphans; a dry run unless `apply`. */
export function useReapImportOrphans() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (apply: boolean) => twinImport.orphans.reap(apply),
    onSuccess: (_res, apply) => {
      if (apply) void qc.invalidateQueries({ queryKey: TWIN_IMPORT_ORPHANS_KEY });
    },
  });
}
